import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Mail, MapPin, MessageCircle, ExternalLink, Ear, Compass, CheckCircle2, Calendar } from "lucide-react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ContactSection from "@/components/sections/ContactSection";
import LocationSection from "@/components/sections/LocationSection";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SITE } from "@/lib/seo";

const TITLE = "Contacto | Vibra Marketing";
const DESCRIPTION =
  "Agenda una llamada gratuita de 20 minutos o escríbenos por WhatsApp. Vibra Marketing, agencia de marketing digital para PYMEs en Madrid.";
const URL = `${SITE.url}/contacto`;

const breadcrumb = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Inicio", item: `${SITE.url}/` },
    { "@type": "ListItem", position: 2, name: "Contacto", item: URL },
  ],
};

const contactPage = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  url: URL,
  name: TITLE,
  description: DESCRIPTION,
};

const localBusiness = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${SITE.url}/#localbusiness`,
  name: "Vibra Marketing S.L.",
  image: SITE.ogImage,
  url: SITE.url,
  telephone: SITE.whatsapp,
  email: "direccion@vibramarketing.es",
  vatID: "B25913948",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Calle San Roque 4",
    postalCode: "28710",
    addressLocality: "El Molar",
    addressRegion: "Madrid",
    addressCountry: "ES",
  },
  areaServed: { "@type": "Country", name: "España" },
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "09:00",
    closes: "18:00",
  },
};

const steps = [
  {
    Icon: Ear,
    title: "Te escuchamos",
    desc: "Nos cuentas tu negocio, tu situación y tus objetivos. Unos 15 minutos.",
  },
  {
    Icon: Compass,
    title: "Te orientamos",
    desc: "Te decimos qué haríamos en tu caso y con qué servicios.",
  },
  {
    Icon: CheckCircle2,
    title: "Tú decides",
    desc: "Sin presión: te enviamos la propuesta y la valoras con calma.",
  },
];

const faqs = [
  {
    q: "¿La llamada tiene coste?",
    a: "No, es gratuita y sin compromiso.",
  },
  {
    q: "¿Tengo que preparar algo?",
    a: "Solo conocer tus números básicos: qué vendes, a quién y qué facturas aproximadamente.",
  },
  {
    q: "¿Atendéis fuera de Madrid?",
    a: "Sí, trabajamos con empresas de toda España de forma remota.",
  },
  {
    q: "¿En qué horario atendéis?",
    a: "De lunes a viernes en horario laboral. Por WhatsApp puedes escribirnos cuando quieras y te respondemos lo antes posible.",
  },
];

const Contacto = () => (
  <div className="min-h-screen bg-background">
    <Helmet>
      <title>{TITLE}</title>
      <meta name="description" content={DESCRIPTION} />
      <link rel="canonical" href={URL} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={TITLE} />
      <meta property="og:description" content={DESCRIPTION} />
      <meta property="og:url" content={URL} />
      <meta property="og:image" content={SITE.ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <script type="application/ld+json">{JSON.stringify(contactPage)}</script>
      <script type="application/ld+json">{JSON.stringify(localBusiness)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>
    </Helmet>
    <Header />
    <main className="pt-24 pb-16">
      {/* Breadcrumb */}
      <div className="container px-6">
        <nav aria-label="Migas de pan" className="mb-6 text-sm text-muted-foreground">
          <ol className="flex flex-wrap items-center gap-2">
            <li><Link to="/" className="hover:text-foreground">Inicio</Link></li>
            <li aria-hidden>/</li>
            <li className="text-foreground font-medium">Contacto</li>
          </ol>
        </nav>
      </div>

      {/* Hero */}
      <header className="container px-6 pt-6 pb-10 text-center max-w-3xl mx-auto">
        <h1 className="font-display text-4xl md:text-6xl uppercase mb-6 leading-[1.05]">
          Hablemos de <span className="text-gradient">tu proyecto</span>
        </h1>
        <p className="text-base md:text-lg text-muted-foreground">
          Llamada gratuita de 20 minutos.{" "}
          <strong className="text-foreground">Sin presión, sin compromiso.</strong>
        </p>
      </header>

      {/* Agenda */}
      <ContactSection hideHeader />

      {/* Vías de contacto: cards con icono grande */}
      <section className="container px-6 pt-16">
        <h2 className="font-display text-3xl md:text-4xl uppercase mb-10 text-center">
          Otras <span className="text-gradient">vías</span>
        </h2>
        <div className="max-w-4xl mx-auto grid sm:grid-cols-3 gap-4">
          {[
            { Icon: Calendar, title: "Agendar llamada", desc: "20 min, gratis", cta: "Ver agenda →", href: "#contacto", internal: true },
            { Icon: MessageCircle, title: "WhatsApp", desc: "+34 614 541 275", cta: "Escríbenos →", href: SITE.whatsappLink },
            { Icon: Mail, title: "Email", desc: "direccion@vibramarketing.es", cta: "Enviar email →", href: "mailto:direccion@vibramarketing.es" },
          ].map(({ Icon, title, desc, cta, href, internal }) => {
            const className = "group flex flex-col items-start gap-3 p-7 rounded-2xl border border-border bg-card hover:border-primary/40 hover:-translate-y-1 transition-all";
            const content = (
              <>
                <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
                  <Icon className="w-6 h-6" />
                </div>
                <div className="font-display text-lg uppercase">{title}</div>
                <div className="text-sm text-muted-foreground break-all flex-1">{desc}</div>
                <div className="text-xs text-primary group-hover:translate-x-1 transition-transform">{cta}</div>
              </>
            );
            return internal ? (
              <a key={title} href={href} className={className}>{content}</a>
            ) : (
              <a key={title} href={href} target="_blank" rel="noopener noreferrer" className={className}>{content}</a>
            );
          })}
        </div>
        <div className="max-w-4xl mx-auto mt-4 text-center">
          <a
            href="https://clientes.vibramarketing.es/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-primary"
          >
            <ExternalLink className="w-3 h-3" /> ¿Ya eres cliente? Accede al panel
          </a>
        </div>
      </section>

      {/* Qué esperar: 3 pasos horizontales */}
      <section className="container px-6 py-20">
        <h2 className="font-display text-3xl md:text-4xl uppercase mb-12 text-center">
          Qué esperar de <span className="text-gradient">la llamada</span>
        </h2>
        <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-5 relative">
          {steps.map(({ Icon, title, desc }, i) => (
            <div
              key={title}
              className="relative p-7 rounded-2xl border border-border bg-card"
            >
              <div className="flex items-center justify-between mb-5">
                <span className="font-display text-3xl text-gradient">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                  <Icon className="w-5 h-5" />
                </div>
              </div>
              <h3 className="font-display text-xl uppercase mb-2">{title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Ubicación + Cómo llegar (SEO local) */}
      <LocationSection />

      {/* FAQ accordion */}
      <section className="container px-6 pb-20">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-display text-3xl md:text-4xl uppercase mb-8 text-center">
            Preguntas <span className="text-gradient">rápidas</span>
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`item-${i}`}>
                <AccordionTrigger className="text-left text-base font-medium">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Datos empresa: discreto */}
      <section className="container px-6 pb-8">
        <div className="max-w-3xl mx-auto flex items-center justify-center gap-2 text-xs text-muted-foreground text-center flex-wrap">
          <MapPin className="text-primary w-3 h-3" />
          <span>
            <strong className="text-foreground">Vibra Marketing S.L.</strong> · CIF B25913948 · Calle San Roque 4, 28710 El Molar (Madrid)
          </span>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export default Contacto;
